import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import callApi, { abortableCallApi } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';
import formatSinglePlural from 'utils/formatSinglePlural';

// Actions
export const FETCH_WORKFLOW_STATUSES = 'FETCH_WORKFLOW_STATUSES';
export const FETCH_WORKFLOW_STATUSES_SUCCESS = 'FETCH_WORKFLOW_STATUSES_SUCCESS';
export const FETCH_WORKFLOW_STATUSES_FAIL = 'FETCH_WORKFLOW_STATUSES_FAIL';
export const CLEAR_WORKFLOW_STATUSES = 'CLEAR_WORKFLOW_STATUSES';
export const CREATE_WORKFLOW_STATUS = 'CREATE_WORKFLOW_STATUS';
export const CREATE_WORKFLOW_STATUS_SUCCESS = 'CREATE_WORKFLOW_STATUS_SUCCESS';
export const CREATE_WORKFLOW_STATUS_FAIL = 'CREATE_WORKFLOW_STATUS_FAIL';
export const UPDATE_WORKFLOW_STATUS = 'UPDATE_WORKFLOW_STATUS';
export const UPDATE_WORKFLOW_STATUS_SUCCESS = 'UPDATE_WORKFLOW_STATUS_SUCCESS';
export const UPDATE_WORKFLOW_STATUS_FAIL = 'UPDATE_WORKFLOW_STATUS_FAIL';
export const DELETE_WORKFLOW_STATUS = 'DELETE_WORKFLOW_STATUS';
export const DELETE_WORKFLOW_STATUS_SUCCESS = 'DELETE_WORKFLOW_STATUS_SUCCESS';
export const DELETE_WORKFLOW_STATUS_FAIL = 'DELETE_WORKFLOW_STATUS_FAIL';
export const REASSIGN_WORKFLOW_STATUS = 'REASSIGN_WORKFLOW_STATUS';
export const REASSIGN_WORKFLOW_STATUS_SUCCESS = 'REASSIGN_WORKFLOW_STATUS_SUCCESS';
export const REASSIGN_WORKFLOW_STATUS_FAIL = 'REASSIGN_WORKFLOW_STATUS_FAIL';

// Reducer
export default function workflowStatusesReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_WORKFLOW_STATUSES:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_WORKFLOW_STATUSES_SUCCESS:
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case FETCH_WORKFLOW_STATUSES_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_WORKFLOW_STATUSES:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case CREATE_WORKFLOW_STATUS_SUCCESS: {
      const newData = [
        ...state.data.slice(0, state.data.length),
        payload,
      ];
      return {
        ...state,
        count: state.count + 1,
        data: newData,
      };
    }
    case UPDATE_WORKFLOW_STATUS_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        // Is Default
        if (dataItem.ID !== payload.ID) {
          return (payload['Is Default'] === true && payload['Workflow State'].ID === dataItem['Workflow State'].ID) ? {
            ...dataItem,
            'Is Default': false,
          } : dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_WORKFLOW_STATUS_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchWorkflowStatuses(payload) {
  return { type: FETCH_WORKFLOW_STATUSES, payload };
}

export function fetchWorkflowStatusesSuccess(payload) {
  return { type: FETCH_WORKFLOW_STATUSES_SUCCESS, payload };
}

export function fetchWorkflowStatusesFail(payload) {
  return { type: FETCH_WORKFLOW_STATUSES_FAIL, payload };
}

export function clearWorkflowStatuses(payload) {
  return { type: CLEAR_WORKFLOW_STATUSES, payload };
}

export function createWorkflowStatus(payload) {
  return { type: CREATE_WORKFLOW_STATUS, payload };
}

export function createWorkflowStatusSuccess(payload) {
  return { type: CREATE_WORKFLOW_STATUS_SUCCESS, payload };
}

export function createWorkflowStatusFail(payload) {
  return { type: CREATE_WORKFLOW_STATUS_FAIL, payload };
}

export function updateWorkflowStatus(payload) {
  return { type: UPDATE_WORKFLOW_STATUS, payload };
}

export function updateWorkflowStatusSuccess(payload) {
  return { type: UPDATE_WORKFLOW_STATUS_SUCCESS, payload };
}

export function updateWorkflowStatusFail(payload) {
  return { type: UPDATE_WORKFLOW_STATUS_FAIL, payload };
}

export function deleteWorkflowStatus(payload) {
  return { type: DELETE_WORKFLOW_STATUS, payload };
}

export function deleteWorkflowStatusSuccess(payload) {
  return { type: DELETE_WORKFLOW_STATUS_SUCCESS, payload };
}

export function deleteWorkflowStatusFail(payload) {
  return { type: DELETE_WORKFLOW_STATUS_FAIL, payload };
}

export function reassignWorkflowStatus(payload) {
  return { type: REASSIGN_WORKFLOW_STATUS, payload };
}

export function reassignWorkflowStatusSuccess(payload) {
  return { type: REASSIGN_WORKFLOW_STATUS_SUCCESS, payload };
}

export function reassignWorkflowStatusFail(payload) {
  return { type: REASSIGN_WORKFLOW_STATUS_FAIL, payload };
}

// Requests

let abort;

export function requestFetchWorkflowStatuses(payload) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = (payload && payload.limit && payload.offset)
    ? abortableCallApi(`workflow/status/?limit=${payload.limit}&offset=${payload.offset}`)
    : abortableCallApi('workflow/status/');
  abort = request.abort;
  return request.res;
}

export function requestSearchWorkflowStatuses({ limit, offset, filters }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('workflow/status/search/', 'POST', {
    filters: filters || [],
    sort_by: [
      {
        name: 'Name',
        sort: 'asc',
      },
      {
        name: 'ID',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
  abort = request.abort;
  return request.res;
}

export function requestCreateWorkflowStatus(payload) {
  return callApi('workflow/status/', 'POST', payload);
}

export function requestUpdateWorkflowStatus(payload) {
  return callApi('workflow/status/', 'PATCH', payload);
}

export function requestDeleteWorkflowStatus(payload) {
  return callApi(`workflow/status/?id=${payload}`, 'DELETE');
}

export function requestReassignWorkflowStatus(payload) {
  return callApi('workflow/reassign/', 'POST', payload);
}

// Sagas
function* fetchWorkflowStatusesSaga(action) {
  const { triggerLoading, filters, limit } = action.payload;
  try {
    const res = filters || limit
      ? yield call(requestSearchWorkflowStatuses, action.payload)
      : yield call(requestFetchWorkflowStatuses, action.payload);
    yield put(fetchWorkflowStatusesSuccess({
      data: res.data,
      count: res.count,
      triggerLoading,
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchWorkflowStatusesFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createWorkflowStatusSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreateWorkflowStatus, values);
    yield put(createWorkflowStatusSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'New Workflow Status successfully added',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createWorkflowStatusFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateWorkflowStatusSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdateWorkflowStatus, values);
    yield put(updateWorkflowStatusSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Workflow Status successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(updateWorkflowStatusFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteWorkflowStatusSaga(action) {
  const { values: { ID }, resolve, reject, showNotification = true } = action.payload;
  try {
    const res = yield call(requestDeleteWorkflowStatus, ID);
    yield put(deleteWorkflowStatusSuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Workflow Status successfully deleted',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(deleteWorkflowStatusFail(err.data));
    if (reject) {
      reject(err.data);
    }
    if (showNotification) {
      yield put(addNotification({
        message: err.data.description || 'Failed to delete Workflow Status',
        variant: 'warning',
      }));
    }
  }
}

function* reassignWorkflowStatusSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestReassignWorkflowStatus, values);
    yield put(reassignWorkflowStatusSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: res.data['updated order lines']
        ? `Workflow Status was successfully reassigned in ${res.data['updated order lines']} order ${formatSinglePlural({
              quantity: res.data['updated order lines'],
              single: 'line',
              plural: 'lines',
            })}`
        : 'Workflow Status was successfully reassigned',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(reassignWorkflowStatusFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

export function* workflowStatusesSaga() {
  yield takeLatest(FETCH_WORKFLOW_STATUSES, fetchWorkflowStatusesSaga);
  yield takeEvery(CREATE_WORKFLOW_STATUS, createWorkflowStatusSaga);
  yield takeEvery(UPDATE_WORKFLOW_STATUS, updateWorkflowStatusSaga);
  yield takeEvery(DELETE_WORKFLOW_STATUS, deleteWorkflowStatusSaga);
  yield takeEvery(REASSIGN_WORKFLOW_STATUS, reassignWorkflowStatusSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/workflowStatuses.js